<template lang="pug">
  .auth--register
    form.auth__form(@submit.prevent="checkForm")
      //- Name input
      input.no-border-bottom(
        type="text"
        name="name"
        v-model="name"
        v-validate="'required|max:35|min:2'"
        :data-vv-as="$t('auth.form.name')"
        :class="{'error': errors.has('name') }"
        :placeholder="$t('auth.form.name')")

      //- Last name input
      input.no-border-bottom(
        type="text"
        name="lastName"
        v-model="lastName"
        v-validate="'required|max:35|min:2'"
        :data-vv-as="$t('auth.form.lastName')"
        :class="{'error': errors.has('lastName') }"
        :placeholder="$t('auth.form.lastName')")

      //- Email input
      input.no-border-bottom(
        type="email"
        name="email"
        v-model="email"
        v-validate="'required|email'"
        :data-vv-as="$t('auth.form.email')"
        :class="{'error': errors.has('email') }"
        :placeholder="$t('auth.form.email')")

      //- Password input
      Password(
        name="password"
        v-model="password"
        v-validate="'required|max:64|min:8'"
        :password="password"
        :data-vv-as="$t('auth.form.password')"
        :error="errors.has('password')")

      //- Errores de validación del formulario
      .margin-halve-top
        .auth__error(v-show="errors.has('name')") {{ errors.first('name') }}
        .auth__error(v-show="errors.has('lastName')") {{ errors.first('lastName') }}
        .auth__error(v-show="errors.has('email')") {{ errors.first('email') }}
        .auth__error(v-show="errors.has('password')") {{ errors.first('password') }}

      //- Recibir notificaciones
      .margin-top {{ $t('auth.authorize') }}

      //- Notificaciones comerciales
      .checkbox.margin-quarter-top.padding-left
        input(
          type="checkbox"
          name="commercial"
          v-model="commercial")

        label(for="commercial")
          | {{ $t('auth.marketing') }}

      //- Notificaciones de soporte
      .checkbox.margin-quarter-top.padding-left(v-if="showSupportEmail")
        input(
          type="checkbox"
          name="support"
          v-model="support")

        label(for="support")
          | {{ $t('auth.support') }}

      hr.padding-top.padding-bottom

      //- Términos de uso
      .margin-halve-bottom {{ $t('auth.agree')}}

      ul.links
        li(v-if="certTerms")
          a.link(href="#" @click="openLinkMixin(certTerms)" target="_blank")
            | {{ $t('auth.terms') }}
        li(v-if="certLicense")
          a.link(href="#" @click="openLinkMixin(certLicense)" target="_blank")
            | {{ $t('auth.license') }}
        li(v-if="certPrivacy")
          a.link(href="#" @click="openLinkMixin(certPrivacy)" target="_blank")
            | {{ $t('auth.privacy') }}


      //- Botón enviar
      van-button.margin-top(
        size="large"
        type="primary"
        native-type="submit") {{ $t('auth.form.send') }}

    //- Enlaces a otras páginas
    .text--center.margin-top.margin-bottom
      router-link.auth__link(to="/auth/login") {{ $t('auth.goToLogin') }}
</template>

<script>
import i18n from '@/locales/i18n';
import { getErrorId } from '@/services/error.service';
import { CONFIG, DOCS } from '@/app.config';
import UserService from '@/services/user.service';
import OpenLinkMixin from '@/mixins/OpenLink.mixin';
import Password from '@/components/Password.vue';

export default {
  components: { Password },
  mixins: [OpenLinkMixin],
  data() {
    return {
      name: '',
      lastName: '',
      email: '',
      password: '',
      toc: true,
      support: true,
      commercial: true,
      authError: null,
      isLoading: false,
      language: i18n.locale,
      showSupportEmail: CONFIG.SHOW_SUPPORT_EMAIL,
    };
  },
  computed: {
    certPrivacy() {
      return DOCS.PRIVACY[this.language.toUpperCase()] || DOCS.PRIVACY.EN;
    },
    certLicense() {
      return DOCS.LEGAL[this.language.toUpperCase()] || DOCS.LEGAL.ES;
    },
    certTerms() {
      return DOCS.TERMS[this.language.toUpperCase()] || DOCS.TERMS.EN;
    },
  },
  methods: {
    /**
     * Compruebo si los datos del formulario son válidos
     */
    checkForm() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.registerUser();
        }
      });
    },

    /*
     * Intento registrar al usuario
     */
    async registerUser() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.authError = null;

        // Obtengo los datos
        const user = {
          email: this.email,
          password: this.password,
          lang: this.language,
          data: {
            name: this.name,
            lastName: this.lastName,
            toc: this.toc,
            commercial: this.commercial,
            support: this.support,
          },
        };

        // Realizo la petición
        await UserService.register(user);

        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('auth.messages.signUp.title'),
            message: i18n.t('auth.messages.signUp.message'),
          })
          .then(() => this.$router.push({ name: 'login' }));
      } catch (error) {
        // Obtengo el error
        this.authError = getErrorId(error);

        this.$toast.clear();

        // Si el usuario no ha confirmado su cuenta,
        // muestro un modal para que pueda volver a recibir el email de confirmación.
        // si el usuario no existe, muestro el error en la vista
        // en cualquier otro caso, muestro un error por defecto
        if (this.authError === 'userNotConfirmed') {
          this.$dialog
            .confirm({
              title: i18n.t(`auth.messages.notValidate.title`),
              message: i18n.t(`auth.messages.notValidate.message`),
            })
            .then(() => this.sendConfirmEmail());
        } else if (this.authError === 'userNotAvailable') {
          this.$dialog.confirm({
            title: i18n.t(`auth.messages.userNotAvailable.title`),
            message: i18n.t(`auth.messages.userNotAvailable.message`),
          });
        } else {
          this.$toast.fail({
            mask: true,
            message: i18n.t('general.errors.default.message'),
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Vuelve a enviar el email de confirmación al usuario
     */
    async sendConfirmEmail() {
      try {
        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Realizo la petición
        await UserService.sendConfirmEmail(this.email);

        // Quito el loader y Confirmo el envío del email
        this.$toast.clear();
        this.$toast.success({
          mask: true,
          message: i18n.t('auth.messages.resendToken'),
        });
      } catch (error) {
        // Ocurrió algún error,
        // quito el loader y muestro un mensaje de error
        this.$toast.clear();
        this.$toast.fail({
          mask: true,
          message: i18n.t('general.errors.default.message'),
        });
      }
    },
  },

  created() {
    window.scrollTo(0, 0);
  },

  destroyed() {
    window.scrollTo(0, 0);
  },
};
</script>

<style lang="scss" scoped>
.links li {
  font-size: 0.9rem;
  list-style-type: disc;
}

.link {
  color: brand();
}
</style>
